import { BotIcon, PauseIcon, ShieldIcon, UserIcon } from 'lucide-react'
import type { ComponentProps } from 'react'

import type {
  ConnectionState,
  ConnectionType,
  LifecycleState,
  MembershipRole,
  MessageState,
} from '@/api/types'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

type BadgeVariant = ComponentProps<typeof Badge>['variant']

/* -------------------------------------------------------------------------- */
/* Lifecycle (§17) — shared by accounts and zones                             */
/* -------------------------------------------------------------------------- */

const LIFECYCLE: Record<LifecycleState, { label: string; variant: BadgeVariant }> = {
  ACTIVE: { label: 'Active', variant: 'success' },
  DEACTIVATED: { label: 'Deactivated', variant: 'secondary' },
  ARCHIVED: { label: 'Archived', variant: 'outline' },
}

/**
 * `ACTIVE | DEACTIVATED | ARCHIVED`. Archived reads as neutral, not alarming —
 * it is a retirement, not a failure — but it is terminal, so it never gets the
 * accent colour that would suggest it can be undone.
 */
export function LifecycleBadge({
  state,
  className,
}: {
  state: LifecycleState
  className?: string
}) {
  const { label, variant } = LIFECYCLE[state]
  return (
    <Badge variant={variant} className={className}>
      {label}
    </Badge>
  )
}

/* -------------------------------------------------------------------------- */
/* Connections (§16)                                                          */
/* -------------------------------------------------------------------------- */

const CONNECTION_TYPE: Record<
  ConnectionType,
  { label: string; icon: typeof BotIcon; hint: string }
> = {
  agent: { label: 'agent', icon: BotIcon, hint: 'Connected over MCP' },
  human: { label: 'human', icon: UserIcon, hint: 'Driven from the web console' },
  police: { label: 'police', icon: ShieldIcon, hint: 'Reserved connection type' },
}

/** The type is as load-bearing as the name now that humans are connections. */
export function ConnectionTypeBadge({ type }: { type: ConnectionType }) {
  const entry = CONNECTION_TYPE[type] as (typeof CONNECTION_TYPE)[ConnectionType] | undefined
  if (entry === undefined) {
    return <Badge variant="outline">{type}</Badge>
  }
  const Icon = entry.icon
  return (
    <Badge variant="outline" className="font-mono" title={entry.hint}>
      <Icon className="size-3" />
      {entry.label}
    </Badge>
  )
}

/**
 * `CONNECTED` pulses; anything else reads **Offline**, not "Disconnected". Since IMPROVEMENTS_1 the
 * only DISCONNECTED rows the console ever sees are reserved identities — the name and the queue
 * are kept, the agent is simply not here — and "disconnected" would suggest the row is a leftover
 * rather than somebody who will be back. The hint says what an offline identity keeps.
 */
export function ConnectionStateBadge({ state }: { state: ConnectionState }) {
  if (state === 'CONNECTED') {
    return (
      <Badge variant="success" className="gap-1.5">
        <span className="size-1.5 animate-pulse rounded-full bg-current" />
        Connected
      </Badge>
    )
  }
  return (
    <Badge
      variant="secondary"
      title="A reserved identity that is not connected right now. It keeps its name, its queue and its channel memberships, and returns as itself with its connection id."
    >
      Offline
    </Badge>
  )
}

/**
 * §19. Sits *beside* the state badge, never instead of it: a paused connection
 * is still CONNECTED and its tools still answer, so replacing "Connected" with
 * "Paused" would state something false and would hide that the agent is still
 * live at the other end of a transport.
 *
 * No pulse. The connected dot animates because the connection is doing
 * something; this one is deliberately still.
 *
 * `destructive` rather than `secondary`: a pause is an enforcement action taken
 * against this agent and it stays in force until a human lifts it, so it must
 * not read as an ordinary idle state somebody can scroll past.
 */
export function PausedBadge({ className }: { className?: string }) {
  return (
    <Badge variant="destructive" className={cn('gap-1.5', className)}>
      <PauseIcon className="size-3" />
      Paused
    </Badge>
  )
}

/* -------------------------------------------------------------------------- */
/* Roles and messages                                                         */
/* -------------------------------------------------------------------------- */

export function RoleBadge({ role }: { role: MembershipRole }) {
  return (
    <Badge variant={role === 'ADMIN' ? 'default' : role === 'BILLING' ? 'outline' : 'secondary'} className="font-mono text-[0.6875rem]">
      {role}
    </Badge>
  )
}

const MESSAGE_STATE: Record<MessageState, { label: string; className: string }> = {
  QUEUED: { label: 'queued', className: 'text-[color-mix(in_oklch,var(--accent)_70%,var(--foreground))]' },
  // §19: written while one end was paused. Not delivered, not lost — waiting on
  // an administrator's DELIVER or DISCARD, so it reads as neither.
  HELD: { label: 'held', className: 'text-[color-mix(in_oklch,var(--destructive)_60%,var(--muted-foreground))]' },
  CONSUMED: { label: 'read', className: 'text-muted-foreground' },
  DISCARDED: { label: 'discarded', className: 'text-destructive' },
  // WI5: the sender's ttl ran out before anybody read it. Not an enforcement action like
  // discarded, so it takes the held tone rather than the destructive one.
  EXPIRED: { label: 'expired', className: 'text-[color-mix(in_oklch,var(--destructive)_60%,var(--muted-foreground))]' },
}

/** Deliberately quiet: the delivery state is metadata, not the message. */
export function MessageStateTag({ state }: { state: MessageState }) {
  const entry = MESSAGE_STATE[state] as (typeof MESSAGE_STATE)[MessageState] | undefined
  return (
    <span className={cn('font-mono text-[0.6875rem] tracking-wide', entry?.className)}>
      {entry?.label ?? state.toLowerCase()}
    </span>
  )
}
